import { Box, Button, Link, Typography } from "@mui/joy";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  darken,
} from "@mui/material";
import { Interactor } from "../entities/missionMap/missionMap";

/**
 * Asks a musician who is about to accept a whole call, with a message that
 * reads like they can only play part of it, whether they meant to Hold.
 *
 * Rendered at every point an answer can actually be sent — the invite's confirm
 * bar and the inbox message that submits straight away — so the message cannot
 * be typed after the button is pressed and slip past.
 *
 * @param {object} props The pending answer and the three ways out of it.
 * @returns {ReactElement} The dialog.
 */
export function PartialAcceptWarning({
  accept,
  hold,
  companyName,
  onHold,
  onAccept,
  onEdit,
}: {
  /** The accept interactor the musician pressed, or undefined when closed. */
  accept?: Interactor;
  hold?: Interactor;
  companyName?: string;
  onHold: () => void;
  onAccept: () => void;
  onEdit: () => void;
}) {
  return (
    <Dialog open={!!accept} onClose={onEdit} sx={{ zIndex: 100005 }}>
      <DialogTitle>
        <Typography level="h4">
          Looks like some dates don&apos;t work?
        </Typography>
      </DialogTitle>
      <DialogContent
        sx={{ gap: 0.5, display: "flex", flexDirection: "column", pt: 0 }}
      >
        {/* One idea per line: what Accept costs, then what Hold gets you. */}
        <Typography level="body-md">
          {accept?.Name} confirms you for every date — no exceptions.
        </Typography>
        <Typography level="body-md">
          {hold?.Name} lets {companyName ?? "them"} work the dates out with you
          first.
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          p: 2,
          pt: 1,
          gap: 1.5,
          flexDirection: "column",
          alignItems: "stretch",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            fullWidth
            variant="outlined"
            color="neutral"
            onClick={onAccept}
          >
            {accept?.Name} anyway
          </Button>
          <Button
            fullWidth
            startDecorator={
              hold?.Icon ? <i className={hold.Icon}></i> : undefined
            }
            sx={{
              background: hold?.Color,
              "&:hover": {
                background: hold?.Color ? darken(hold.Color, 0.1) : undefined,
              },
            }}
            onClick={onHold}
          >
            {hold?.Name} instead
          </Button>
        </Box>
        <Link
          component="button"
          level="body-sm"
          color="neutral"
          onClick={onEdit}
          sx={{ alignSelf: "center" }}
        >
          Edit my message
        </Link>
      </DialogActions>
    </Dialog>
  );
}
